import { useCallback, useEffect, useState } from "react";

import { Icon } from "@blueprintjs/core";
import { Edit3, Eye } from "lucide-react";

import { Box } from "@/components/layout/flexbox";
import { EvaluationCriteriaIcon } from "@/components/pages_logged_in/roleplays/details/evaluation-criteria/EvaluationCriteriaIcon";
import { CardAboveXs } from "@/components/pieces/CardAboveXs";
import { EvaluationCriteriaCard } from "@/components/pieces/evaluation-criteria-manager/EvaluationCriteriaCard";
import { IconCircle } from "@/components/pieces/IconCircle";
import { Button } from "@/components/ui/button";
import { DARK_PURPLE, LIGHT_GRAY } from "@/css/constants";
import { useGetEvaluationCriteriaLazyQuery } from "@/graphql";
import { EvaluationCriteria } from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";

import { EvaluationCriteriaItemModalsProvider } from "./EvaluationCriteriaItemModals";
import {
  EvaluationCriteriaModalsProvider,
  useEvaluationCriteriaModalsContext,
} from "./EvaluationCriteriaModals";
import { EvaluationCriteriaToolbar } from "./EvaluationCriteriaToolbar";

interface EvaluationCriteriaManagerInnerProps {
  scenarioId: string;
  canEdit: boolean;
  criteria: EvaluationCriteria[];
  refetch: () => void;
}

function EvaluationCriteriaManagerInner({
  scenarioId,
  canEdit,
  criteria,
  refetch,
}: EvaluationCriteriaManagerInnerProps) {
  const [isEditing, setIsEditing] = useState(false);
  const isEditMode = isEditing && canEdit;
  const { handleAdd } = useEvaluationCriteriaModalsContext();

  return (
    <>
      <div className="flex flex-col justify-between gap-4 lg:flex-row">
        <div>
          <div className="flex items-center text-xl font-bold">
            <IconCircle
              icon={<EvaluationCriteriaIcon />}
              backgroundColor={LIGHT_GRAY}
              color={DARK_PURPLE}
              size={32}
              mr={2}
            />
            <div>Evaluation Criteria</div>
          </div>
          <div className="mt-2 leading-relaxed text-gray-700">
            Your session will be assessed with feedback in these areas
          </div>
        </div>
        {canEdit && (
          <div>
            <div className="flex gap-0.5 rounded-lg border border-gray-300 bg-white p-0.5">
              <Button
                variant="minimal"
                intent="secondary"
                size="sm"
                style={{ width: 80 }}
                onClick={() => setIsEditing(false)}
                className={`gap-2 transition-all ${
                  !isEditMode
                    ? "bg-blue-200 text-blue-700 hover:bg-blue-200 hover:text-blue-700"
                    : "text-gray-500 hover:bg-blue-200/50 hover:text-gray-700"
                }`}
              >
                <Eye size={14} />
                View
              </Button>
              <Button
                variant="minimal"
                intent="secondary"
                size="sm"
                style={{ width: 80 }}
                onClick={() => setIsEditing(true)}
                className={`gap-2 transition-all ${
                  isEditMode
                    ? "bg-blue-200 text-blue-700 hover:bg-blue-200 hover:text-blue-700"
                    : "text-gray-500 hover:bg-blue-200/50 hover:text-gray-700"
                }`}
              >
                <Edit3 size={14} />
                Edit
              </Button>
            </div>
          </div>
        )}
      </div>

      {isEditMode && (
        <Box mt={4}>
          <EvaluationCriteriaToolbar criteria={criteria} />
        </Box>
      )}

      <div className="mt-6 flex flex-col gap-4">
        {criteria?.map((criterion) => (
          <EvaluationCriteriaItemModalsProvider
            key={criterion.id}
            criterion={criterion}
            refetch={refetch}
          >
            <EvaluationCriteriaCard
              entityType="scenario"
              scenarioId={scenarioId}
              criterion={criterion}
              viewMode={isEditMode ? "edit" : "simple"}
              refetch={refetch}
            />
          </EvaluationCriteriaItemModalsProvider>
        ))}
        {criteria.length === 0 && (
          <div className="rounded-md border border-dashed border-gray-400 px-6 py-20 text-center">
            <div className="semibold">No criteria yet</div>
            <div className="mx-auto mt-2 max-w-96 text-gray-500">
              Add evaluation criteria to define how learners will be assessed in
              this scenario. You can import from a rubric, generate based on
              your description, or create your own.
            </div>
            {isEditMode && (
              <Button
                variant="minimal"
                size="sm"
                className="mt-6"
                onClick={handleAdd}
              >
                <Icon icon="plus" />
                Add Criteria
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

interface EvaluationCriteriaManagerProps {
  scenarioId: string;
  canEdit: boolean;
}

export function EvaluationCriteriaManager({
  scenarioId,
  canEdit = false,
}: EvaluationCriteriaManagerProps) {
  const [getEvaluationCriteria, { data: criteriaData }] =
    useGetEvaluationCriteriaLazyQuery();

  const criteria = ensureNonNullArray(criteriaData?.evaluationCriteria);

  useEffect(() => {
    getEvaluationCriteria({
      variables: {
        scenarioId: scenarioId,
      },
      fetchPolicy: "cache-and-network",
    });
  }, [scenarioId, getEvaluationCriteria]);

  const refetch = useCallback(() => {
    getEvaluationCriteria({
      variables: {
        scenarioId: scenarioId,
      },
      fetchPolicy: "network-only",
    });
  }, [getEvaluationCriteria, scenarioId]);

  return (
    <CardAboveXs>
      <EvaluationCriteriaModalsProvider
        entityType="scenario"
        scenarioId={scenarioId}
        criteria={criteria}
        refetch={refetch}
      >
        <EvaluationCriteriaManagerInner
          scenarioId={scenarioId}
          canEdit={canEdit}
          criteria={criteria}
          refetch={refetch}
        />
      </EvaluationCriteriaModalsProvider>
    </CardAboveXs>
  );
}
